import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export const useSubscription = () => {
  const [subscription, setSubscription] = useState(null);
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isLocked, setIsLocked] = useState(false);
  const [daysUntilExpiry, setDaysUntilExpiry] = useState(null);

  const fetchSubscriptionStatus = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/subscription/status`, { withCredentials: true });
      setSubscription(response.data.subscription);
      setUsage(response.data.usage);
      setIsLocked(response.data.is_locked || false);
      setDaysUntilExpiry(response.data.days_until_expiry);
      setError(null);
    } catch (err) {
      console.error('Error fetching subscription:', err);
      setError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSubscriptionStatus();
  }, [fetchSubscriptionStatus]);

  const tier = subscription?.tier || 'free';
  const isPro = tier === 'pro' || tier === 'enterprise';
  const isEnterprise = tier === 'enterprise';
  
  // Limits come from backend usage stats (driven by TIER_LIMITS)
  const programsUsed = usage?.programs_count || 0;
  const programsLimit = usage?.programs_limit ?? 3;
  const athletesUsed = usage?.athletes_count || 0;
  const athletesLimit = usage?.athletes_limit ?? 30;

  // -1 means unlimited
  const isAtProgramLimit = programsLimit > 0 && programsUsed >= programsLimit;
  const isAtAthleteLimit = athletesLimit > 0 && athletesUsed >= athletesLimit;

  // Approaching limit (80%+)
  const isApproachingProgramLimit = programsLimit > 0 && programsUsed >= Math.floor(programsLimit * 0.8) && !isAtProgramLimit;
  const isApproachingAthleteLimit = athletesLimit > 0 && athletesUsed >= Math.floor(athletesLimit * 0.8) && !isAtAthleteLimit;

  // Show upgrade banner when approaching or at limit
  const shouldShowUpgradeBanner = 
    isApproachingProgramLimit || 
    isApproachingAthleteLimit || 
    isAtProgramLimit || 
    isAtAthleteLimit;

  // Check if subscription is expiring soon (within 7 days)
  const isExpiringSoon = isPro && daysUntilExpiry !== null && daysUntilExpiry <= 7 && daysUntilExpiry >= 0;

  return {
    subscription,
    usage,
    loading,
    error,
    refresh: fetchSubscriptionStatus,
    tier,
    isPro,
    isEnterprise,
    programsUsed,
    programsLimit,
    athletesUsed,
    athletesLimit,
    isAtProgramLimit,
    isAtAthleteLimit,
    isApproachingProgramLimit,
    isApproachingAthleteLimit,
    shouldShowUpgradeBanner,
    canAddProgram: programsLimit < 0 || programsUsed < programsLimit,
    canAddAthlete: athletesLimit < 0 || athletesUsed < athletesLimit,
    isLocked,
    daysUntilExpiry,
    isExpiringSoon,
  };
};

export default useSubscription;
